import { useEffect, useState } from "react";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";

type AthleteOption = { id: string; name: string; published: boolean };

const AdminUpdates = () => {
  const [athletes, setAthletes] = useState<AthleteOption[]>([]);
  const [athleteId, setAthleteId] = useState("");
  const [message, setMessage] = useState("");
  const [published, setPublished] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    supabase.from("athletes").select("id,name,published").order("name").then(({ data }) => {
      setAthletes((data ?? []) as AthleteOption[]);
    });
  }, []);

  const loadUpdate = async (id: string) => {
    setAthleteId(id);
    if (!id) return;
    const { data } = await supabase.from("athlete_status").select("message,published").eq("athlete_id", id).maybeSingle();
    setMessage(data?.message ?? "");
    setPublished(data?.published ?? false);
  };

  const save = async () => {
    const clean = message.replace(/<[^>]*>/g, "").trim();
    if (!athleteId || !clean) return toast.error("Choose an athlete and enter an update.");
    if (clean.length > 500) return toast.error("Updates are limited to 500 characters.");
    setSaving(true);
    const { error } = await supabase.from("athlete_status").upsert({
      athlete_id: athleteId,
      message: clean,
      published,
      updated_at: new Date().toISOString(),
    });
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success(published ? "Update published to My Corner." : "Draft update saved.");
  };

  return (
    <AdminLayout>
      <div className="max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Fan updates</p>
        <h1 className="mt-2 font-display text-3xl">Athlete updates</h1>
        <p className="mt-2 text-sm text-muted-foreground">Create one current update per athlete. Only published updates appear in My Corner.</p>
        <div className="mt-6 space-y-5 rounded-lg border border-border bg-card p-5 shadow-card">
          <div>
            <label htmlFor="update-athlete" className="text-sm font-semibold">Athlete</label>
            <select id="update-athlete" value={athleteId} onChange={(event) => loadUpdate(event.target.value)} className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2">
              <option value="">Choose an athlete</option>
              {athletes.map((athlete) => <option key={athlete.id} value={athlete.id}>{athlete.name}{athlete.published ? "" : " (draft profile)"}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="update-message" className="text-sm font-semibold">Update</label>
            <textarea id="update-message" value={message} onChange={(event) => setMessage(event.target.value)} maxLength={500} rows={5} className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2" />
            <p className="mt-1 text-xs text-muted-foreground">{message.length}/500</p>
          </div>
          <label className="flex items-start gap-3 text-sm">
            <Checkbox checked={published} onCheckedChange={(value) => setPublished(value === true)} />
            <span><strong>Publish this update.</strong> Followers who opted in will see it and receive an in-app notification.</span>
          </label>
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : published ? "Publish update" : "Save draft"}</Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUpdates;